/**
 * Página: WahaMonitor
 * Monitoramento em tempo real das sessões WAHA
 * Espelho do dashboard do WAHA para monitoramento interno
 * 
 * @version 1.0.0
 * @created 2026-01-05
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, Server, Clock, Wifi } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';

import { useWahaMonitor } from '@/hooks/useWahaMonitor';
import { WahaStatusCards } from '@/components/admin/waha-monitor/WahaStatusCards';
import { WahaHealthAlerts } from '@/components/admin/waha-monitor/WahaHealthAlerts';
import { WahaSessionsTable } from '@/components/admin/waha-monitor/WahaSessionsTable';

/**
 * Página de monitoramento WAHA
 */
export default function WahaMonitor() {
  const { t } = useTranslation('sidebar');
  
  // Estado de auto-refresh
  const [autoRefresh, setAutoRefresh] = useState(false);
  const refreshInterval = autoRefresh ? 30000 : 0; // 30 segundos se habilitado
  
  // Hook de monitoramento
  const {
    data,
    sessions,
    summary,
    alerts,
    wahaServer,
    verifiedAt,
    isLoading,
    isFetching,
    refetch,
    syncStatus,
    isSyncing,
    deleteOrphan,
    isDeleting,
  } = useWahaMonitor(true, refreshInterval);

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Cabeçalho */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {t('admin.items.wahaMonitor', 'Monitor WAHA')}
          </h1>
          <p className="text-muted-foreground">
            Monitoramento em tempo real das sessões do servidor WAHA
          </p>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Auto-refresh toggle */}
          <div className="flex items-center gap-2">
            <Switch
              id="auto-refresh"
              checked={autoRefresh}
              onCheckedChange={setAutoRefresh}
            />
            <Label htmlFor="auto-refresh" className="text-sm text-muted-foreground">
              Auto-refresh (30s)
            </Label>
          </div>
          
          {/* Botão de atualizar */}
          <Button 
            variant="outline" 
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Informações do servidor */}
      {isLoading ? (
        <div className="flex gap-4">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-10 w-48" />
        </div>
      ) : data && (
        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Server className="h-4 w-4" />
            <span>Servidor: <code className="bg-muted px-2 py-0.5 rounded">{wahaServer}</code></span>
          </div>
          
          <div className="flex items-center gap-2">
            <Wifi className="h-4 w-4" />
            <span>{data.totalWahaSessions} sessões no WAHA</span>
          </div>
          
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            <span>
              Verificado {verifiedAt && formatDistanceToNow(new Date(verifiedAt), {
                addSuffix: true,
                locale: ptBR,
              })}
            </span>
          </div>
        </div>
      )}

      {/* Cards de Status */}
      <WahaStatusCards summary={summary} isLoading={isLoading} />

      {/* Alertas de Inconsistência */}
      {!isLoading && <WahaHealthAlerts alerts={alerts} />}

      {/* Tabela de Sessões */}
      <Card className="overflow-hidden">
        <CardHeader>
          <CardTitle>Comparação WAHA vs Local</CardTitle>
          <CardDescription>
            Lista de todas as sessões do servidor WAHA comparadas com as inboxes locais
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-64 w-full" />
            </div>
          ) : (
            <WahaSessionsTable
              sessions={sessions}
              onSyncStatus={syncStatus}
              onDeleteOrphan={deleteOrphan}
              isSyncing={isSyncing}
              isDeleting={isDeleting}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
